/**
 * Outil « Circuit RC » (Boîte à outils, physique-chimie Tle) — logique PURE.
 *
 * Un générateur de tension E, un conducteur ohmique R, un condensateur C et un interrupteur à
 * deux positions : charge (u_C de 0 vers E) ou décharge (u_C de E vers 0). Solutions exactes de
 * R × C × du_C/dt + u_C = E (ou 0) : u_C, i, q = C × u_C, τ = R × C, 63 % / 37 % à t = τ,
 * régime permanent après environ 5τ.
 */

import { frNum, mcqOptions, pick, retryMcq, type Mcq, type Rng } from '../../ui/mcq';

export const fr = frNum;
const NB = String.fromCharCode(0xa0);

export type Phase = 'charge' | 'discharge';

/** valeurs normalisées des composants du labo */
export const R_VALUES = [100, 220, 470, 1000, 2200, 4700, 10_000] as const; // Ω
export const C_VALUES = [1, 2.2, 4.7, 10, 22, 47, 100, 470] as const; // µF

export const tauOf = (R: number, CuF: number) => R * CuF * 1e-6; // s

export interface RcState {
  /** V */
  uC: number;
  uR: number;
  /** A (sens de la charge positif) */
  i: number;
  /** C (coulombs) */
  q: number;
}

export function rcAt(phase: Phase, E: number, R: number, CuF: number, t: number): RcState {
  const tau = tauOf(R, CuF);
  const x = Math.exp(-t / tau);
  const C = CuF * 1e-6;
  if (phase === 'charge') {
    const uC = E * (1 - x);
    return { uC, uR: E - uC, i: (E / R) * x, q: C * uC };
  }
  const uC = E * x;
  return { uC, uR: -uC, i: -(E / R) * x, q: C * uC };
}

/** durée affichée : 5τ (régime permanent atteint à 1 % près) */
export const span = (R: number, CuF: number) => 5 * tauOf(R, CuF);

/** temps lisible : µs, ms ou s */
export function timeText(t: number): string {
  if (t === 0) return `0${NB}s`;
  if (t < 1e-3) return `${fr(t * 1e6, t * 1e6 < 10 ? 1 : 0)}${NB}µs`;
  if (t < 1) return `${fr(t * 1e3, t * 1e3 < 10 ? 2 : 1)}${NB}ms`;
  return `${fr(t, 2)}${NB}s`;
}
export const ohm = (R: number) => (R >= 1000 ? `${fr(R / 1000, 1)}${NB}kΩ` : `${fr(R, 0)}${NB}Ω`);
export const microF = (CuF: number) => `${fr(CuF, 1)}${NB}µF`;
export function ampText(i: number): string {
  const a = Math.abs(i);
  if (a >= 1) return `${fr(i, 2)}${NB}A`;
  if (a >= 1e-3) return `${fr(i * 1e3, 2)}${NB}mA`;
  return `${fr(i * 1e6, 1)}${NB}µA`;
}
export const coulombText = (q: number) =>
  q >= 1e-3 ? `${fr(q * 1e3, 2)}${NB}mC` : `${fr(q * 1e6, 1)}${NB}µC`;

export function rcLines(phase: Phase, E: number, R: number, CuF: number, t: number): string[] {
  const tau = tauOf(R, CuF);
  const s = rcAt(phase, E, R, CuF, t);
  const ratio = t / tau;
  const out = [
    `τ = R × C = ${ohm(R)} × ${microF(CuF)} = ${timeText(tau)}.`,
    phase === 'charge'
      ? `Charge : R × C × du_C/dt + u_C = E, d’où u_C(t) = E × (1 − e^(−t/τ)) = ${fr(s.uC, 2)}${NB}V.`
      : `Décharge : R × C × du_C/dt + u_C = 0, d’où u_C(t) = E × e^(−t/τ) = ${fr(s.uC, 2)}${NB}V.`,
  ];
  if (Math.abs(ratio - 1) < 0.04)
    out.push(
      phase === 'charge'
        ? `À t = τ, u_C atteint 63 % de E (${fr(0.632 * E, 2)}${NB}V) : c’est ainsi qu’on lit τ sur la courbe.`
        : `À t = τ, u_C n’est plus que 37 % de E (${fr(0.368 * E, 2)}${NB}V) : c’est ainsi qu’on lit τ sur la courbe.`,
    );
  else if (ratio >= 4.9)
    out.push(
      'Après environ 5τ, le régime permanent est atteint : u_C ne varie plus, i = 0 ; le condensateur se comporte comme un interrupteur ouvert.',
    );
  else
    out.push(
      `t = ${fr(ratio, 2)} τ : la tangente à l’origine coupe l’asymptote ${phase === 'charge' ? 'u_C = E' : 'u_C = 0'} à t = τ.`,
    );
  out.push(
    `i = dq/dt = C × du_C/dt = ${ampText(s.i)}${phase === 'discharge' ? ' (le courant circule dans l’autre sens)' : ''} ; q = C × u_C = ${coulombText(s.q)}.`,
  );
  return out;
}

/* ---------- défi ---------- */

export type QuizKind = 'tau' | 'tau-graph' | 'value-at' | 'charge-q' | 'current' | 'diff-eq';

export type Quiz = Mcq<QuizKind>;

export function quizKinds(_rank: number | null): QuizKind[] {
  return ['tau', 'tau-graph', 'value-at', 'charge-q', 'current', 'diff-eq'];
}

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  return retryMcq((r) => buildQuiz(kind, r), rng);
}

type W = readonly [string, string];
const q = (
  kind: QuizKind,
  question: string,
  good: string,
  wrongs: W[],
  explain: string,
  rng: Rng,
): Quiz => ({ kind, question, options: mcqOptions(good, wrongs, rng), explain });

function buildQuiz(kind: QuizKind, rng: Rng): Quiz {
  switch (kind) {
    case 'tau': {
      const R = pick([1000, 2200, 4700, 10_000], rng);
      const C = pick([10, 47, 100], rng);
      const tau = tauOf(R, C);
      return q(
        kind,
        `R = ${ohm(R)} et C = ${microF(C)}. Constante de temps τ du circuit ?`,
        timeText(tau),
        [
          [timeText(R * C), 'C est en microfarads : 1 µF = 10⁻⁶ F.'],
          [timeText(tau * Math.LN2), 'τ × ln 2, c’est le temps pour que u_C atteigne E/2.'],
          [timeText(5 * tau), '5τ, c’est la durée pour atteindre le régime permanent.'],
        ],
        `τ = R × C = ${R}${NB}Ω × ${fr(C, 1)} × 10⁻⁶${NB}F = ${timeText(tau)}.`,
        rng,
      );
    }
    case 'tau-graph': {
      const E = pick([5, 6, 12], rng);
      const tau = pick([10, 20, 50], rng); // ms
      return q(
        kind,
        `Charge sous E = ${E}${NB}V : u_C atteint ${fr(0.63 * E, 1)}${NB}V à t = ${tau}${NB}ms, puis ${fr(0.99 * E, 2)}${NB}V vers ${5 * tau}${NB}ms. Que vaut τ ?`,
        `${tau}${NB}ms`,
        [
          [`${5 * tau}${NB}ms`, '5τ correspond au régime permanent, pas à τ.'],
          [`${fr(tau / 2, 1)}${NB}ms`, 'À t = τ, u_C vaut 63 % de E (et pas 50 %).'],
          [`${fr(0.63 * tau, 1)}${NB}ms`, 'C’est u_C qui vaut 63 % de E, pas le temps.'],
        ],
        `À t = τ, u_C = 63 % de E = ${fr(0.63 * E, 1)}${NB}V : τ = ${tau}${NB}ms.`,
        rng,
      );
    }
    case 'value-at': {
      const E = pick([5, 6, 10, 12], rng);
      const charge = rng() < 0.5;
      const v = charge ? 0.63 * E : 0.37 * E;
      return q(
        kind,
        charge
          ? `Charge d’un condensateur sous E = ${E}${NB}V, initialement déchargé. u_C à t = τ ?`
          : `Décharge d’un condensateur chargé sous ${E}${NB}V. u_C à t = τ ?`,
        `${fr(v, 2)}${NB}V`,
        [
          [
            `${fr(charge ? 0.37 * E : 0.63 * E, 2)}${NB}V`,
            charge
              ? 'En charge, u_C a parcouru 63 % du chemin vers E.'
              : 'En décharge, il reste 37 % de E.',
          ],
          [`${fr(E / 2, 2)}${NB}V`, 'u_C = E/2 à t = τ × ln 2 ≈ 0,69 τ, pas à τ.'],
          [
            `${fr(charge ? E : 0, 2)}${NB}V`,
            'Il faut environ 5τ pour atteindre le régime permanent.',
          ],
        ],
        charge
          ? `u_C(τ) = E × (1 − e⁻¹) ≈ 0,63 × ${E} = ${fr(v, 2)}${NB}V.`
          : `u_C(τ) = E × e⁻¹ ≈ 0,37 × ${E} = ${fr(v, 2)}${NB}V.`,
        rng,
      );
    }
    case 'charge-q': {
      const C = pick([10, 47, 100], rng);
      const u = pick([5, 6, 12], rng);
      const Q = C * u; // µC
      return q(
        kind,
        `Un condensateur de ${microF(C)} est chargé sous u_C = ${u}${NB}V. Charge q de son armature positive ?`,
        `${fr(Q, 0)}${NB}µC`,
        [
          [`${fr(C / u, 2)}${NB}µC`, 'q = C × u_C, on multiplie.'],
          [`${fr(Q, 0)}${NB}C`, 'C est en µF : q est en µC.'],
          [`${fr(Q / 2, 0)}${NB}µC`, 'q = C × u (pas ½ C u, qui est une énergie).'],
        ],
        `q = C × u_C = ${fr(C, 0)} × 10⁻⁶ × ${u} = ${fr(Q, 0)}${NB}µC.`,
        rng,
      );
    }
    case 'current': {
      const dq = pick([20, 50, 100], rng); // µC
      const dt = pick([2, 5, 10], rng); // ms
      const I = dq / dt; // mA
      return q(
        kind,
        `La charge du condensateur augmente de ${dq}${NB}µC en ${dt}${NB}ms (variation régulière). Intensité moyenne du courant ?`,
        `${fr(I, 1)}${NB}mA`,
        [
          [`${fr(dq * dt, 0)}${NB}mA`, 'i = Δq ÷ Δt, on divise.'],
          [`${fr(dt / dq, 2)}${NB}mA`, 'i = Δq ÷ Δt, pas l’inverse.'],
          [`${fr(I * 1000, 0)}${NB}mA`, 'Vérifie les unités : µC ÷ ms = mA.'],
        ],
        `i = dq/dt ≈ ${dq} × 10⁻⁶${NB}C ÷ ${dt} × 10⁻³${NB}s = ${fr(I, 1)}${NB}mA.`,
        rng,
      );
    }
    case 'diff-eq': {
      const charge = rng() < 0.5;
      return q(
        kind,
        charge
          ? 'Quelle équation différentielle vérifie u_C pendant la charge sous la tension E ?'
          : 'Quelle équation différentielle vérifie u_C pendant la décharge ?',
        charge ? 'R × C × du_C/dt + u_C = E' : 'R × C × du_C/dt + u_C = 0',
        [
          charge
            ? ['R × C × du_C/dt + u_C = 0', 'C’est l’équation de la décharge (plus de générateur).']
            : [
                'R × C × du_C/dt + u_C = E',
                'C’est l’équation de la charge : le générateur est débranché.',
              ],
          [
            'du_C/dt = R × C × u_C',
            'Loi des mailles : u_R + u_C = E (ou 0), avec u_R = R × i = R × C × du_C/dt.',
          ],
          ['R × du_C/dt + C × u_C = E', 'R et C se multiplient : τ = R × C.'],
        ],
        `Loi des mailles : R × i + u_C = ${charge ? 'E' : '0'}, avec i = C × du_C/dt.`,
        rng,
      );
    }
  }
}
